import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Mail, Check, X, Pencil, ChevronDown, ChevronUp, Users, User } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../../api/client';
import type { Event, Collaborator, EventTeam, EventTeamSlot, CollaboratorTemplate } from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { Input, Select } from '../../../components/ui/Input';
import { PaymentStatusBadge } from '../../../components/ui/Badge';
import { EmptyState } from '../../../components/ui/EmptyState';
import { UserAvatar } from '../../../components/ui/UserAvatar';
import { formatCurrency, formatDate } from '../../../utils/format';

export default function CollaboratorsTab({ event }: { event: Event }) {
  const qc = useQueryClient();
  const teams: EventTeam[] = event.teams ?? [];
  const multiTeam = teams.length > 1;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['event', event.id] });

  // — collab list (always loaded for selects)
  const { data: allCollaborators = [] as unknown as Collaborator[] } = useQuery({
    queryKey: ['collaborators-list'],
    queryFn: async () => {
      const res = await apiGet<{ data: Collaborator[] }>('/collaborators?activeOnly=true');
      return (res as unknown as { data: Collaborator[] }).data ?? res;
    },
  });

  // — template list (for "Apply template" modal)
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const { data: templates = [] as unknown as CollaboratorTemplate[] } = useQuery({
    queryKey: ['collaborator-templates'],
    queryFn: async () => {
      const res = await apiGet<{ data: CollaboratorTemplate[] }>('/collaborator-templates');
      return (res as unknown as { data: CollaboratorTemplate[] }).data ?? res;
    },
    enabled: showTemplateModal,
  });

  const applyTemplateMutation = useMutation({
    mutationFn: () => apiPost(`/events/${event.id}/teams/from-template`, { templateId: selectedTemplateId }),
    onSuccess: () => {
      invalidate();
      setShowTemplateModal(false);
      setSelectedTemplateId('');
      toast.success('Template applicato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // — Add team
  const addTeamMutation = useMutation({
    mutationFn: () => apiPost(`/events/${event.id}/teams`, {}),
    onSuccess: () => { invalidate(); toast.success('Squadra aggiunta'); },
    onError: (e: Error) => toast.error(e.message),
  });

  // — Delete team
  const deleteTeamMutation = useMutation({
    mutationFn: (teamId: string) => apiDelete(`/events/${event.id}/teams/${teamId}`),
    onSuccess: () => { invalidate(); toast.success('Squadra rimossa'); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button
          variant="secondary"
          size="sm"
          icon={<Users size={15} />}
          onClick={() => setShowTemplateModal(true)}
        >
          Applica template
        </Button>
        <Button
          size="sm"
          icon={<Plus size={15} />}
          onClick={() => addTeamMutation.mutate()}
          loading={addTeamMutation.isPending}
        >
          Aggiungi squadra
        </Button>
      </div>

      {teams.length === 0 ? (
        <EmptyState
          icon={<Users size={32} />}
          title="Nessuna squadra assegnata"
          description="Usa i pulsanti sopra per applicare un template o aggiungere una squadra"
        />
      ) : (
        teams.map(team => (
          <TeamBlock
            key={team.id}
            event={event}
            team={team}
            multiTeam={multiTeam}
            allCollaborators={allCollaborators}
            onDeleteTeam={() => deleteTeamMutation.mutate(team.id)}
            invalidate={invalidate}
          />
        ))
      )}

      {/* Apply template modal */}
      <Modal
        open={showTemplateModal}
        onClose={() => { setShowTemplateModal(false); setSelectedTemplateId(''); }}
        title="Applica template collaboratori"
        footer={
          <>
            <Button variant="ghost" onClick={() => { setShowTemplateModal(false); setSelectedTemplateId(''); }}>
              Annulla
            </Button>
            <Button
              onClick={() => applyTemplateMutation.mutate()}
              disabled={!selectedTemplateId}
              loading={applyTemplateMutation.isPending}
            >
              Applica
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Select
            label="Template"
            value={selectedTemplateId}
            onChange={e => setSelectedTemplateId(e.target.value)}
            placeholder="Seleziona template…"
            options={templates.map(t => ({
              value: t.id,
              label: t.eventType ? `${t.name} (${t.eventType})` : t.name,
            }))}
          />
          {selectedTemplateId && (() => {
            const t = templates.find(x => x.id === selectedTemplateId);
            if (!t) return null;
            return (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {t.teams.map(team => (
                  <div key={team.id}>
                    {team.name && <strong>{team.name}: </strong>}
                    {team.roles.map(r => r.role).join(', ')}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </Modal>
    </div>
  );
}

// ─── TeamBlock ───────────────────────────────────────────────────────────────

function TeamBlock({
  event,
  team,
  multiTeam,
  allCollaborators,
  onDeleteTeam,
  invalidate,
}: {
  event: Event;
  team: EventTeam;
  multiTeam: boolean;
  allCollaborators: Collaborator[];
  onDeleteTeam: () => void;
  invalidate: () => void;
}) {
  const [editingObjectivo, setEditingObjectivo] = useState(false);
  const [objInput, setObjInput] = useState(team.obiettivo ?? '');
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [slotForm, setSlotForm] = useState({ role: '', collaboratorId: '', customName: '', fee: '' });

  const updateTeamMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPatch(`/events/${event.id}/teams/${team.id}`, data),
    onSuccess: () => { invalidate(); setEditingObjectivo(false); },
    onError: (e: Error) => toast.error(e.message),
  });

  const addSlotMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPost(`/events/${event.id}/teams/${team.id}/slots`, data),
    onSuccess: () => {
      invalidate();
      setShowAddSlot(false);
      setSlotForm({ role: '', collaboratorId: '', customName: '', fee: '' });
      toast.success('Ruolo aggiunto');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div style={{
      background: 'var(--glass-bg-card)',
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)',
      overflow: 'hidden',
    }}>
      {/* Team header */}
      <div style={{
        background: 'var(--glass-bg-card)',
        padding: '0.625rem 1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        borderBottom: '1px solid rgba(255,255,255,0.10)',
      }}>
        <Users size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
        {multiTeam ? (
          editingObjectivo ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, flexWrap: 'wrap' }}>
              <input
                value={objInput}
                onChange={e => setObjInput(e.target.value)}
                placeholder="Obiettivo squadra…"
                autoFocus
                style={{
                  flex: 1,
                  padding: '0.2rem 0.5rem',
                  fontSize: 'var(--text-sm)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 'var(--radius-interactive)',
                  background: 'var(--color-input-bg, var(--glass-bg-card))',
                  color: 'var(--color-text)',
                }}
              />
              <button
                onClick={() => updateTeamMutation.mutate({ obiettivo: objInput || null })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)', display: 'flex', padding: '0.375rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
              >
                <Check size={14} />
              </button>
              <button
                onClick={() => { setEditingObjectivo(false); setObjInput(team.obiettivo ?? ''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex', padding: '0.375rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1 }}>
              <span style={{ fontWeight: 500, fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
                {team.obiettivo || <span style={{ color: 'var(--color-text-faint)', fontStyle: 'italic' }}>Squadra senza obiettivo</span>}
              </span>
              <button
                onClick={() => setEditingObjectivo(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex', padding: '0.375rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
              >
                <Pencil size={11} />
              </button>
            </div>
          )
        ) : (
          <span style={{ fontWeight: 500, fontSize: 'var(--text-sm)', color: 'var(--color-text)', flex: 1 }}>
            Squadra
          </span>
        )}
        <button
          onClick={onDeleteTeam}
          title="Elimina squadra"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex', padding: '0.375rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Slots */}
      <div style={{ padding: '0.5rem 0' }}>
        {team.slots.length === 0 ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', padding: '0.75rem 1rem', margin: 0, fontStyle: 'italic' }}>
            Nessun ruolo — aggiungi un ruolo sotto
          </p>
        ) : (
          team.slots.map(slot => (
            <SlotRow
              key={slot.id}
              slot={slot}
              event={event}
              team={team}
              allCollaborators={allCollaborators}
              invalidate={invalidate}
            />
          ))
        )}

        {/* Add slot row */}
        {showAddSlot ? (
          <div style={{ padding: '0.75rem 1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.10)' }}>
            <div style={{ flex: '1 1 6rem', minWidth: '6rem' }}>
              <Input
                label="Ruolo *"
                value={slotForm.role}
                onChange={e => setSlotForm(f => ({ ...f, role: e.target.value }))}
                placeholder="es. Fotografo principale"
              />
            </div>
            <div style={{ flex: '1 1 8rem', minWidth: '8rem' }}>
              {slotForm.collaboratorId === '__adhoc__' ? (
                <Input
                  label="Nome collaboratore"
                  value={slotForm.customName}
                  onChange={e => setSlotForm(f => ({ ...f, customName: e.target.value }))}
                  placeholder="es. Marco Rossi"
                />
              ) : (
                <Select
                  label="Collaboratore"
                  value={slotForm.collaboratorId}
                  onChange={e => {
                    const val = e.target.value;
                    setSlotForm(f => ({ ...f, collaboratorId: val, customName: val === '__adhoc__' ? f.customName : '' }));
                  }}
                  placeholder="Nessuno (assegna dopo)"
                  options={[
                    ...allCollaborators.map(c => ({ value: c.id, label: `${c.name} — ${c.role}` })),
                    { value: '__adhoc__', label: 'Altro...' },
                  ]}
                />
              )}
            </div>
            <div style={{ flex: '0 0 6rem' }}>
              <Input
                label="Compenso (€)"
                type="number" min="0" step="0.01"
                value={slotForm.fee}
                onChange={e => setSlotForm(f => ({ ...f, fee: e.target.value }))}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', alignSelf: 'flex-end', paddingBottom: '0.1rem' }}>
              <Button
                size="sm"
                onClick={() => {
                  if (!slotForm.role.trim()) { toast.error('Inserisci il ruolo'); return; }
                    addSlotMutation.mutate({
                      role: slotForm.role,
                      collaboratorId: slotForm.collaboratorId === '__adhoc__' ? undefined : (slotForm.collaboratorId || undefined),
                      customName: slotForm.collaboratorId === '__adhoc__' ? slotForm.customName || undefined : undefined,
                      fee: slotForm.fee ? Number(slotForm.fee) : undefined,
                    });
                }}
                loading={addSlotMutation.isPending}
              >
                Aggiungi
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowAddSlot(false); setSlotForm({ role: '', collaboratorId: '', customName: '', fee: '' }); }}>
                Annulla
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '0.5rem 0.75rem', borderTop: team.slots.length > 0 ? '1px solid rgba(255,255,255,0.10)' : undefined }}>
            <button
              onClick={() => setShowAddSlot(true)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 'var(--text-sm)', color: 'var(--color-primary)',
                display: 'flex', alignItems: 'center', gap: '0.3rem',
              }}
            >
              <Plus size={13} /> Aggiungi ruolo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SlotRow ─────────────────────────────────────────────────────────────────

function SlotRow({
  slot,
  event,
  team,
  allCollaborators,
  invalidate,
}: {
  slot: EventTeamSlot;
  event: Event;
  team: EventTeam;
  allCollaborators: Collaborator[];
  invalidate: () => void;
}) {
  const [editingFee, setEditingFee] = useState(false);
  const [feeInput, setFeeInput] = useState('');
  const [showCollabSelect, setShowCollabSelect] = useState(false);
  const [collabSelectValue, setCollabSelectValue] = useState(slot.collaboratorId ?? (slot.customName ? '__adhoc__' : ''));
  const [adhocNameInput, setAdhocNameInput] = useState(slot.customName ?? '');
  const [showPartialModal, setShowPartialModal] = useState(false);
  const [newPartialAmount, setNewPartialAmount] = useState('');
  const [newPartialDate, setNewPartialDate] = useState(new Date().toISOString().slice(0, 10));

  const patchSlot = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPatch<{ data: EventTeamSlot & { warning?: string } }>(
        `/events/${event.id}/teams/${team.id}/slots/${slot.id}`,
        data,
      ),
    onSuccess: (res) => {
      invalidate();
      const r = res as { warning?: string };
      if (r?.warning) toast.warning(r.warning);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patchPayment = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPatch(`/events/${event.id}/teams/${team.id}/slots/${slot.id}/payment`, data),
    onSuccess: () => { invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteSlot = useMutation({
    mutationFn: () => apiDelete(`/events/${event.id}/teams/${team.id}/slots/${slot.id}`),
    onSuccess: () => { invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const inviteSlot = useMutation({
    mutationFn: () => apiPost(`/events/${event.id}/teams/${team.id}/slots/${slot.id}/invite`),
    onSuccess: () => { invalidate(); toast.success('Invito inviato!'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const effectiveFee = slot.agreedFee != null ? slot.agreedFee : slot.proposedFee;
  const hasOverride = slot.agreedFee != null;

  return (
    <>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.5rem 1rem',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      flexWrap: 'wrap',
    }}>
      {/* Role label */}
      <div style={{ minWidth: '6rem', flex: '1 1 6rem' }}>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{slot.role}</span>
      </div>

      {/* Collaborator */}
      <div style={{ flex: '1 1 8rem', minWidth: 0 }}>
        {showCollabSelect ? (
          <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <select
                value={collabSelectValue}
                onChange={e => {
                  setCollabSelectValue(e.target.value);
                  if (e.target.value !== '__adhoc__') setAdhocNameInput('');
                }}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: 'var(--text-sm)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 'var(--radius-interactive)',
                  background: 'var(--glass-bg-card)',
                  color: 'var(--color-text)',
                }}
              >
                <option value="">Nessuno</option>
                {allCollaborators.map(c => (
                  <option key={c.id} value={c.id}>{c.name} — {c.role}</option>
                ))}
                <option value="__adhoc__">Altro...</option>
              </select>
              {collabSelectValue === '__adhoc__' && (
                <input
                  type="text"
                  value={adhocNameInput}
                  onChange={e => setAdhocNameInput(e.target.value)}
                  placeholder="Nome collaboratore esterno..."
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: 'var(--text-sm)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 'var(--radius-interactive)',
                    background: 'var(--glass-bg-card)',
                    color: 'var(--color-text)',
                  }}
                />
              )}
            </div>
            <button
              onClick={() => {
                const isAdhoc = collabSelectValue === '__adhoc__';
                patchSlot.mutate({
                  collaboratorId: isAdhoc ? null : (collabSelectValue || null),
                  customName: isAdhoc ? (adhocNameInput.trim() || null) : null,
                });
                setShowCollabSelect(false);
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)', display: 'flex', padding: '0.375rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)', marginTop: collabSelectValue === '__adhoc__' ? '0' : undefined }}
            >
              <Check size={14} />
            </button>
            <button
              onClick={() => { setShowCollabSelect(false); setCollabSelectValue(slot.collaboratorId ?? (slot.customName ? '__adhoc__' : '')); setAdhocNameInput(slot.customName ?? ''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex', padding: '0.375rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
            >
              <X size={14} />
            </button>
          </div>
        ) : slot.collaborator ? (
          <button
            onClick={() => { setCollabSelectValue(slot.collaboratorId ?? ''); setShowCollabSelect(true); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text)', fontSize: 'var(--text-sm)' }}
          >
            <UserAvatar avatarUrl={slot.collaborator.avatarUrl} name={slot.collaborator.name} size={24} />
            <span style={{ fontWeight: 500 }}>{slot.collaborator.name}</span>
            <Pencil size={11} style={{ color: 'var(--color-text-faint)' }} />
          </button>
        ) : slot.customName ? (
          <button
            onClick={() => { setCollabSelectValue(slot.customName ? '__adhoc__' : ''); setAdhocNameInput(slot.customName ?? ''); setShowCollabSelect(true); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text)', fontSize: 'var(--text-sm)' }}
          >
            <User size={20} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
            <span style={{ fontWeight: 500, fontStyle: 'italic' }}>{slot.customName}</span>
            <Pencil size={11} style={{ color: 'var(--color-text-faint)' }} />
          </button>
        ) : (
          <button
            onClick={() => { setCollabSelectValue(''); setShowCollabSelect(true); }}
            style={{
              background: 'none',
              border: '1px dashed rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-interactive)',
              cursor: 'pointer',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-primary)',
              padding: '0.2rem 0.5rem',
              display: 'flex', alignItems: 'center', gap: '0.25rem',
            }}
          >
            <Plus size={11} /> Assegna
          </button>
        )}
      </div>

      {/* Fee */}
      <div style={{ flex: '0 0 auto', minWidth: '5rem' }}>
        {editingFee ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
            <input
              type="number" min="0" step="0.01"
              value={feeInput}
              onChange={e => setFeeInput(e.target.value)}
              autoFocus
              style={{
                flex: '1 1 5rem',
                minWidth: '4rem',
                maxWidth: '8rem',
                padding: '0.2rem 0.4rem',
                fontSize: 'var(--text-sm)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-interactive)',
                background: 'var(--glass-bg-card)',
                color: 'var(--color-text)',
              }}
            />
            <button
              onClick={() => { patchSlot.mutate({ agreedFee: feeInput !== '' ? Number(feeInput) : null }); setEditingFee(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)', display: 'flex', padding: '0.25rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
            >
              <Check size={13} />
            </button>
            <button
              onClick={() => setEditingFee(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex', padding: '0.25rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
            >
              <X size={13} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => { setFeeInput(effectiveFee != null ? String(effectiveFee) : ''); setEditingFee(true); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            title="Modifica compenso"
          >
            {effectiveFee != null ? (
              <span style={{ fontWeight: 600, color: 'var(--color-gold)', fontSize: 'var(--text-sm)' }}>
                {formatCurrency(effectiveFee)}
                {hasOverride && <span style={{ fontSize: '0.65rem', marginLeft: '0.2rem', color: 'var(--color-warning)' }}>*</span>}
              </span>
            ) : (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>—</span>
            )}
            <Pencil size={10} style={{ color: 'var(--color-text-faint)' }} />
          </button>
        )}
      </div>

      {/* Payment status */}
      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
        <PaymentStatusBadge status={slot.feeStatus} partialPayments={slot.partialPayments} totalFee={effectiveFee} />
        {slot.feeStatus === 'PARZIALE' && (
          <button
            onClick={() => {
              setNewPartialAmount('');
              setNewPartialDate(new Date().toISOString().slice(0, 10));
              setShowPartialModal(true);
            }}
            title="Aggiungi pagamento"
            style={{
              background: 'var(--color-gold-highlight)', border: 'none', cursor: 'pointer',
              color: 'var(--color-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '20px', height: '20px', borderRadius: 'var(--radius-full)',
              fontSize: '14px', fontWeight: 700, lineHeight: 1, padding: 0, flexShrink: 0,
            }}
          >
            +
          </button>
        )}
        <select
          value={slot.feeStatus}
          onChange={e => {
            const newStatus = e.target.value;
            if (newStatus === 'PARZIALE') {
              setNewPartialAmount('');
              setNewPartialDate(new Date().toISOString().slice(0, 10));
              setShowPartialModal(true);
              return;
            }
            patchPayment.mutate({ feeStatus: newStatus });
          }}
          style={{
            fontSize: 'var(--text-xs)', padding: '0.15rem 0.3rem',
            border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)',
            background: 'var(--glass-bg-card)', color: 'var(--color-text-muted)', cursor: 'pointer',
          }}
        >
          <option value="DA_PAGARE">Da pagare</option>
          <option value="PARZIALE">Parziale</option>
          <option value="PAGATO">Pagato</option>
        </select>
      </div>

      {/* Actions */}
      <div style={{ flex: '0 0 auto', display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
        {slot.collaborator && !slot.inviteSent && (
          <button
            onClick={() => inviteSlot.mutate()}
            title="Invia invito"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.375rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
          >
            <Mail size={13} />
          </button>
        )}
        {slot.inviteSent && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: '0.15rem' }} title={slot.inviteSentAt ? formatDate(slot.inviteSentAt) : undefined}>
            <Mail size={11} /> ✓
          </span>
        )}
        <button
          onClick={() => deleteSlot.mutate()}
          title="Rimuovi ruolo"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex', padding: '0.375rem', flexShrink: 0, borderRadius: 'var(--radius-interactive)' }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>

    {/* Partial payment modal */}
    {(() => {
      const prevPayments = slot.partialPayments ?? [];
      const totalPaid = prevPayments.reduce((s, p) => s + p.amount, 0);
      return (
    <Modal
      open={showPartialModal}
      onClose={() => setShowPartialModal(false)}
      title="Pagamento parziale"
      width="420px"
      footer={
        <>
          <Button variant="ghost" onClick={() => setShowPartialModal(false)}>
            Annulla
          </Button>
          <Button
            onClick={() => {
              const amount = Number(newPartialAmount);
              if (!newPartialAmount || amount <= 0) {
                toast.error('Inserisci un importo valido');
                return;
              }
              if (!newPartialDate) {
                toast.error('Inserisci la data di pagamento');
                return;
              }
              if (new Date(newPartialDate) > new Date()) {
                toast.error('La data di pagamento non può essere futura');
                return;
              }
              if (effectiveFee != null && effectiveFee > 0) {
                const newTotal = totalPaid + amount;
                if (newTotal > effectiveFee) {
                  toast.error(`Il pagamento (${formatCurrency(amount)}) supera il residuo (${formatCurrency(effectiveFee - totalPaid)})`);
                  return;
                }
              }
              const newTotalPaid = totalPaid + amount;
              const feeStatus = effectiveFee != null && effectiveFee > 0 && newTotalPaid >= effectiveFee ? 'PAGATO' as const : 'PARZIALE' as const;
              patchPayment.mutate({
                feeStatus,
                partialPayments: [
                  ...prevPayments,
                  { amount, paidAt: new Date(newPartialDate).toISOString() },
                ],
              });
              setShowPartialModal(false);
            }}
          >
            Aggiungi pagamento
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Payment history table */}
        {prevPayments.length > 0 && (
          <div>
            <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
              Cronologia pagamenti
            </div>
            <table style={{ width: '100%', fontSize: 'var(--text-sm)', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.10)' }}>
                  <th style={{ textAlign: 'left', padding: '0.35rem 0.5rem', color: 'var(--color-text-faint)', fontWeight: 500, fontSize: 'var(--text-xs)' }}>Data</th>
                  <th style={{ textAlign: 'right', padding: '0.35rem 0.5rem', color: 'var(--color-text-faint)', fontWeight: 500, fontSize: 'var(--text-xs)' }}>Importo</th>
                </tr>
              </thead>
              <tbody>
                {prevPayments.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <td style={{ padding: '0.35rem 0.5rem' }}>{formatDate(p.paidAt)}</td>
                    <td style={{ textAlign: 'right', padding: '0.35rem 0.5rem', fontWeight: 500, color: 'var(--color-gold)' }}>{formatCurrency(p.amount)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid rgba(255,255,255,0.10)', fontWeight: 600 }}>
                  <td style={{ padding: '0.4rem 0.5rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Totale</td>
                  <td style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--color-gold)' }}>{formatCurrency(totalPaid)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* New payment form */}
        <div style={{ borderTop: prevPayments.length > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none', paddingTop: prevPayments.length > 0 ? '0.75rem' : 0 }}>
          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
            {prevPayments.length > 0 ? 'Nuovo pagamento' : 'Dettagli pagamento'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <Input
              label="Importo pagato (€)"
              type="number" min="0" step="0.01"
              value={newPartialAmount}
              onChange={e => setNewPartialAmount(e.target.value)}
              placeholder="es. 150.00"
              autoFocus
            />
            <Input
              label="Data pagamento"
              type="date"
              value={newPartialDate}
              onChange={e => setNewPartialDate(e.target.value)}
            />
          </div>
        </div>

        {/* Summary */}
        {effectiveFee != null && effectiveFee > 0 && (
          <div style={{
            background: 'var(--glass-bg-card)',
            borderRadius: 'var(--radius-interactive)',
            padding: '0.5rem 0.75rem',
            fontSize: 'var(--text-xs)',
            display: 'flex', justifyContent: 'space-between', gap: '1rem',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}>
            <span style={{ color: 'var(--color-text-muted)' }}>
              Compenso: {formatCurrency(effectiveFee)}
            </span>
            <span style={{ color: effectiveFee - totalPaid <= 0 ? 'var(--color-success)' : 'var(--color-warning)', fontWeight: 500 }}>
              {effectiveFee - totalPaid <= 0 ? 'Completato' : `Rimanente: ${formatCurrency(effectiveFee - totalPaid)}`}
            </span>
          </div>
        )}
      </div>
    </Modal>
      );
    })()}
  </>
  );
}
